<template>
  <div
    class="video-widget"
    :class="widgetClasses"
    :style="{ ...widgetStyles, textAlign: widget.alignment || 'center' }"
  >
    <div v-if="widget.title" class="video-title mb-2">{{ widget.title }}</div>

    <!-- YouTube Video -->
    <div
      v-if="widget.videoType === 'youtube' && youtubeId"
      class="video-container responsive-video"
      :style="iframeContainerStyle"
    >
      <iframe
        :src="`https://www.youtube.com/embed/${youtubeId}?autoplay=${widget.autoplay ? 1 : 0}&rel=0&modestbranding=1`"
        :title="widget.title || 'YouTube Video'"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        loading="lazy"
      ></iframe>
    </div>

    <!-- Vimeo Video -->
    <div
      v-else-if="widget.videoType === 'vimeo' && vimeoId"
      class="video-container responsive-video"
      :style="iframeContainerStyle"
    >
      <iframe
        :src="`https://player.vimeo.com/video/${vimeoId}?autoplay=${widget.autoplay ? 1 : 0}&title=0&byline=0&portrait=0`"
        :title="widget.title || 'Vimeo Video'"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
        loading="lazy"
      ></iframe>
    </div>

    <!-- MP4 Video -->
    <div v-else-if="widget.videoType === 'mp4'" class="video-container" :style="mp4ContainerStyle">
      <video
        :src="widget.videoUrl"
        :autoplay="widget.autoplay"
        :loop="widget.loop"
        :muted="widget.muted"
        :controls="widget.controls !== false"
        :poster="widget.thumbnail"
        class="custom-video"
        playsinline
      >
        Ο browser σας δεν υποστηρίζει video.
      </video>
    </div>

    <!-- Invalid video -->
    <div v-else class="invalid-video text-muted text-center p-4 border border-dashed rounded">
      <i class="bi bi-exclamation-triangle me-2"></i>
      <span>Μη έγκυρη διεύθυνση βίντεο</span>
      <br>
      <small class="text-break">{{ widget.videoUrl }}</small>
    </div>

    <div v-if="widget.caption" class="video-caption">{{ widget.caption }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { VideoWidget } from '@/types/pagebuilder'
import { useWidgetStyles } from '@/composables/useWidgetStyles'

interface Props {
  widget: VideoWidget
}

const props = defineProps<Props>()
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

const iframeContainerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.widget.width) {
    style.width = props.widget.width
    style.maxWidth = props.widget.width
  }
  if (props.widget.height) {
    style.paddingBottom = '0'
    style.height = props.widget.height
  }
  return style
})

const mp4ContainerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.widget.width) {
    style.width = props.widget.width
    style.maxWidth = props.widget.width
  }
  if (props.widget.height) {
    style.height = props.widget.height
  }
  return style
})

const youtubeId = computed(() => {
  if (props.widget.videoType !== 'youtube') return null
  
  const url = props.widget.videoUrl
  if (!url) return null
  
  // Extract YouTube video ID from various URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  
  return null
})

const vimeoId = computed(() => {
  if (props.widget.videoType !== 'vimeo') return null
  
  const url = props.widget.videoUrl
  if (!url) return null
  
  // Extract Vimeo video ID
  const match = url.match(/(?:vimeo\.com\/)(\d+)/)
  return match ? match[1] : null
})
</script>

<style scoped>
.video-widget {
  margin: 1rem 0;
}

.video-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.video-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.video-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.responsive-video {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  display: block;
  width: 100%;
  max-width: 100%;
}

.responsive-video iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.custom-video {
  max-width: 100%;
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
}

.video-caption {
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
  text-align: center;
}

.invalid-video {
  background: #f8f9fa;
  color: #6c757d;
}

@media (max-width: 768px) {
  .video-container {
    border-radius: 8px;
  }
  
  .video-caption {
    font-size: 0.85rem;
  }
}
</style>